import React, { useState, useEffect, useMemo } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { FaPlus } from "react-icons/fa";
import styles from "./styles/QuestionDetails.module.scss";
import { useForm, Controller } from "react-hook-form";
import {
  Button,
  Input,
  LabelHeader,
  Select,
  UploadButton,
} from "../../Components/CustomComponent";
import { useMutation } from "react-query";
import { Toast } from "../../Hooks/useToast";
import { QuestionAPI } from "../../Api/Question";
import { useAppContext } from "../../Context/AppContext";
import { useLocation, useNavigate } from "react-router-dom";
import { GeneralApi } from "../../Api/General";
import { ReactComponent as CameraIcon } from "../../Components/CustomComponent/images/camera.svg";
import { OPTIONS_TYPES, QUESTION_TYPES, STRING, WEB_ROUTES } from "../../constants";
import { baseRoute } from "../../Helper";
import _ from "lodash";

export default function QuestionDetails() {
  const questionAPI = QuestionAPI();
  const generalApi = GeneralApi();
  const location = useLocation();

  const question = useMemo(() => location.state?.question, []);
  const quiz_id = useMemo(() => location.state?.quiz_id, [])
  const isMultileFileUpload = useMemo(() => false, []);
  const fileAcceptParams = useMemo(() => "image/png, image/jpeg", []);

  const [coverImage, setCoverImage] = useState(null);
  const [options, setOptions] = useState(question?.options?.length ? question?.options : [
    { option: "", option_type: OPTIONS_TYPES.TEXT, image_url: "", is_correct: true },
    { option: "", option_type: OPTIONS_TYPES.TEXT, image_url: "", is_correct: false },
  ]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [questionType, setQuestionType] = useState(question?.question_type || QUESTION_TYPES.MCQ);
  const [OptionType, setOptionType] = useState(question?.options?.length ? question?.options[0].option_type : OPTIONS_TYPES.TEXT);


  const { control, watch, handleSubmit, setValue } = useForm({
    mode: "onChange",
    defaultValues: {
      question: question?.question,
      question_type: question?.question_type,
      answer: question?.answer,
      points: question?.points,
      duration: question?.duration,
      image_url: question?.image_url || '',
    }
  });

  const cover_image_url = watch('image_url')

  useEffect(() => {
    if (!quiz_id) {
      return navigate(baseRoute(WEB_ROUTES.QUIZ), { replace: true })
    }
  }, [])


  const handleCoverImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverImage(file);
    }
  }

  const handleAddfilesAndSelect = (index, field, e) => {
    const file = e.target.files[0];
    if (file) {
      setOptions((prev) => {
        return prev.map((opt, ind) => {
          if (index !== ind) return opt
          return {
            ...opt,
            image_url: file,
          }
        })
      });
    }
  };

  const handleOptionClick = (index) => {
    setOptions((prev) => {
      return prev.map((opt, i) => {
        return {
          ...opt,
          is_correct: i == index
        }
      })
    });
  };


  const questionMutation = useMutation(questionAPI.create, {
    onSuccess: (data) => {
      Toast("Question added successfully!", "success", false);
      navigate(-1);
    },
    onError: (err) => {
      Toast(err.message, "error", false);
      setLoading(false);
    },
  });


  const uploadMutation = useMutation(generalApi.upload, {
    onSuccess: (data) => { },
    onError: (err) => {
      Toast(err.message, "error", false);
      setLoading(false);
    },
  });


  const handleQuestionType = (value) => {
    if (value === QUESTION_TYPES.MCQ) {
      setValue('answer', null)
    }
    else {
      setValue('options', [])
    }
    setQuestionType(value)
  }


  const handleOptionChange = (index, field, value) => {
    const updatedOptions = [...options];
    updatedOptions[index][field] = value;
    setOptions(updatedOptions);
  };

  const handleAddOption = () => {
    setOptions([
      ...options,
      { option: "", option_type: "BLANK", image_url: "", is_correct: false },
    ]);
  };

  const onSubmit = async (data) => {
    if (loading) return;
    try {
      setLoading(true);


      if (coverImage) {
        const formDataForUpload = new FormData();
        formDataForUpload.append("file", coverImage);
        formDataForUpload.append("path", "quizzes");

        const uploadResponse = await uploadMutation.mutateAsync(
          formDataForUpload
        );
        data.image_url = uploadResponse?.data[0];
      }

      const uploadOptions = _.cloneDeep(options)
      const uploadPromises = uploadOptions.map((opt, index) => {
        if (_.isObject(opt.image_url)) {
          const formData = new FormData();
          formData.append("file", opt.image_url);
          formData.append("path", "quizzes");

          return uploadMutation.mutateAsync(formData).then((uploadData) => {
            const imageUrl = uploadData?.data[0];
            opt.image_url = imageUrl
            return opt
          });
        }

        return opt;
      });
      await Promise.all(uploadPromises);

      const requestBody = {
        quiz_id: quiz_id,
        image_url: data?.image_url,
        question: data.question,
        question_type: questionType,
        answer: data.answer,
        points: Number(data.points),
        duration: Number(data.duration),
        options: questionType !== QUESTION_TYPES.MCQ ? [] : uploadOptions.map((opt, idx) => ({
          option_type: OptionType,
          option: opt.option,
          image_url: opt.image_url,
          is_correct: opt.is_correct,
        })),
      };


      await questionMutation.mutate(requestBody);
    }
    catch (err) {
      setLoading(false)
      Toast(err.message || STRING.server_error, 'error', false)
    }
  };


  return (
    <Container className={styles.QuestionDetails}>
      <LabelHeader text="Question Details" classes="ls-6 mb-2" />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col sm={12}>
            <UploadButton
              label="Upload Cover"
              classes="my-2"
              labelClasses="p-5"
              isMultileFileUpload={false}
              fileAcceptParams={fileAcceptParams}
              file={coverImage || cover_image_url}
              handleFileChange={handleCoverImageSelect}
            />
          </Col>
          <Col sm={12}>
            <Controller
              name="question"
              control={control}
              render={({ field }) => (
                <Input {...field} label="Question" placeholder="Add here" />
              )}
            />
          </Col>
          <Col sm={12}>
            <Controller
              name="question_type"
              control={control}
              // disabled={true}
              render={({ field }) => (
                <Select
                  {...field}
                  label="Question Type"
                  options={[
                    { label: "MCQ", value: QUESTION_TYPES.MCQ },
                    { label: "BLANK", value: QUESTION_TYPES.BLANK },
                  ]}
                  selected={questionType}
                  handleChange={(value) => {
                    field.onChange(value);
                    handleQuestionType(value);
                  }}
                // isDownIcon={false}
                />
              )}
            />
          </Col>
          {questionType === QUESTION_TYPES.MCQ
            ?
            <>
              <Col sm={12}>
                <Controller
                  name="option_type"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="Option Type"
                      options={[
                        { label: "TEXT", value: 10 },
                        { label: "IMAGE", value: 20 },
                      ]}
                      selected={OptionType}
                      handleChange={(value) => {
                        field.onChange(value);
                        setOptionType(value);
                      }}
                      isDownIcon={true}
                    />
                  )}
                />
              </Col>

              {options.map((option, index) => (
                <Col sm={12}>
                  <Controller
                    name={`option_${index}_option`}
                    control={control}
                    render={({ field }) => (
                      <>
                        {OptionType === OPTIONS_TYPES.TEXT ? (
                          <Input
                            {...field}
                            value={option.option}
                            label={`Option ${index + 1}`}
                            placeholder="Add Here"
                            onChange={(e) =>
                              handleOptionChange(index, "option", e.target.value)
                            }
                            onClick={() => handleOptionClick(index)}
                            isActiveOption={option.is_correct}
                            isOption={true}
                          />
                        ) : (
                          <div className={`${styles.UploadButton} mb-2`} key={index}>
                            <input
                              type="file"
                              id={`file-${index + 1}`}
                              className={styles.uploadInput}
                              onChange={(e) => handleAddfilesAndSelect(index, "image_url", e)}
                              multiple={isMultileFileUpload}
                              accept={fileAcceptParams}
                            />
                            <label
                              htmlFor={`file-${index + 1}`}
                              className={`${styles.uploadLabel} ${option?.image_url ? styles.fileUploaded : ""}`}
                            >
                              {
                                option?.image_url ?
                                  <img
                                    src={_.isObject(option.image_url) ? URL.createObjectURL(option.image_url) : option?.image_url}
                                    // className={styles.imagePreview}
                                    alt="Preview"
                                    style={{
                                      height: "100%",
                                      aspectRatio: 1
                                    }}
                                  /> :
                                  <>
                                    <span className={styles.icon}>
                                      <CameraIcon />
                                    </span>
                                    <span>{`Option ${index + 1}`}</span>
                                  </>
                              }
                            </label>
                            <span
                              className={`${styles.optionIcon} ${option.is_correct ? styles.active : ""
                                }`}
                              onClick={() => handleOptionClick(index)} // Toggle selection
                            ></span>
                          </div>

                        )}
                      </>
                    )}
                  />
                </Col>
              ))}
              <Col sm={12}>
                <LabelHeader
                  classes="text-yellow mt-2 cursor-pointer fs-6"
                  text={
                    <div className="d-flex flex-row align-items-center gap-2">
                      <span>
                        <FaPlus size={15} />
                      </span>
                      <span onClick={handleAddOption}>Add New Option</span>
                    </div>
                  }
                />
              </Col>
            </>
            :
            <Col sm={12}>
              <Controller
                name="answer"
                control={control}
                render={({ field }) => (
                  <Input {...field} label="Answer" placeholder="Add here" />
                )}
              />
            </Col>
          }
        </Row>
        <LabelHeader text="Points And Duration" classes="ls-6 mt-5 mb-2" />
        <Row>
          <Col sm={12}>
            <Controller
              name="duration"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Question Duration"
                  placeholder="15 sec"
                  type="number"
                />
              )}
            />
          </Col>
          <Col sm={12}>
            <Controller
              name="points"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Question Points"
                  placeholder="1"
                  type="number"
                />
              )}
            />
          </Col>
        </Row>

        <Button type="submit" text="Submit" classes="mt-2" isSubmitted={loading} />
      </form>
    </Container>
  );
}
